// Cálculos de Volumen a partir de get_industry_volume_pulse. Todo
// es "a la fecha": la temporada hasta la semana del último zarpe cargado y la
// anterior cortada en la misma semana.

export type PulseExporter = {
  key: string;
  name: string;
  mine: boolean;
  cur: number | null;
  prev: number | null;
  prev_total: number | null;
  cur_air: number | null;
  cur_non_china: number | null;
  prev_non_china: number | null;
  first_etd: string | null;
  by_variety: Record<string, number> | null;
};

export type PulseTransit = {
  arrived: number | null;
  d1_7: number | null;
  d8_14: number | null;
  d15: number | null;
  unknown: number | null;
  mine_arrived: number | null;
  mine_pending: number | null;
  mine_total: number | null;
  prev_pending: number | null;
  prev_total: number | null;
};

export type PulseData = {
  has_data: boolean;
  // full: suscrita a ASOEX (todo). preview: solo los totales de industry.
  access?: 'full' | 'preview';
  industry?: { cur: number | null; prev: number | null; prev_total: number | null; exporters: number | null };
  season: string;
  // etd: por semana de zarpe. eta: por semana de arribo.
  basis?: 'etd' | 'eta';
  transit?: PulseTransit;
  prev_season?: string;
  last_etd?: string;
  cutoff_index?: number;
  cutoff_week?: number;
  prev_cutoff_etd?: string | null;
  mine?: number | null;
  mine_name?: string | null;
  top_varieties?: string[];
  curves?: { s: string; i: number; v: string; k: number }[];
  exporters?: PulseExporter[];
  varieties?: {
    v: string;
    cur: number | null;
    prev: number | null;
    mine: number | null;
    peak_cur: number | null;
    peak_prev: number | null;
  }[];
  markets?: {
    region: string;
    country: string;
    port?: string;
    cur: number | null;
    prev: number | null;
    mine: number | null;
  }[];
  // Las variedades que van juntas en OTRAS, una por una.
  varieties_other?: { v: string; cur: number | null; prev: number | null; mine: number | null }[];
};

export const OTHERS = 'OTRAS';

const n = (value: number | null | undefined) => value ?? 0;
const ratio = (a: number, b: number) => (b ? a / b : null);
const growth = (cur: number, prev: number) => (prev ? cur / prev - 1 : null);

// Nombre para mostrar: sin razón social y en tipo título ("LO GARCES LTDA." →
// "Lo Garces"). Los nombres de nuestros clientes vienen limpios.
export const displayName = (raw: string) => {
  const clean = raw
    .replace(/\b(S\.?\s?A\.?|S\.?P\.?A\.?|SPA|SP|LTDA\.?|LIMITADA)\s*$/i, '')
    .replace(/[\s.,-]+$/, '')
    .trim();
  if (clean !== clean.toUpperCase()) return clean;
  return clean
    .toLowerCase()
    .replace(/(^|[\s.&(/-])([a-záéíóúñ])/g, (_, sep, ch) => sep + ch.toUpperCase());
};

// Las claves de la exportadora del usuario se juntan en una sola fila.
export const mergedExporters = (data: PulseData): PulseExporter[] => {
  const rows = data.exporters ?? [];
  const mine = rows.filter((e) => e.mine);
  const others = rows.filter((e) => !e.mine);
  if (!mine.length) return others;
  const sum = (pick: (e: PulseExporter) => number | null) =>
    mine.reduce((total, e) => total + n(pick(e)), 0);
  const byVariety: Record<string, number> = {};
  mine.forEach((e) =>
    Object.entries(e.by_variety ?? {}).forEach(([v, k]) => {
      byVariety[v] = (byVariety[v] ?? 0) + k;
    })
  );
  return [
    ...others,
    {
      key: mine.map((e) => e.key).join('+'),
      name: data.mine_name || mine[0].name,
      mine: true,
      cur: sum((e) => e.cur),
      prev: sum((e) => e.prev),
      prev_total: sum((e) => e.prev_total),
      cur_air: sum((e) => e.cur_air),
      cur_non_china: sum((e) => e.cur_non_china),
      prev_non_china: sum((e) => e.prev_non_china),
      first_etd: mine.map((e) => e.first_etd).filter(Boolean).sort()[0] ?? null,
      by_variety: byVariety,
    },
  ];
};

const rankOf = (rows: PulseExporter[], pick: (e: PulseExporter) => number) => {
  const sorted = rows.filter((e) => pick(e) > 0).sort((a, b) => pick(b) - pick(a));
  const index = sorted.findIndex((e) => e.mine);
  return { position: index === -1 ? null : index + 1, of: sorted.length };
};

export const pulseKpis = (data: PulseData) => {
  const rows = mergedExporters(data);
  const industryCur = rows.reduce((t, e) => t + n(e.cur), 0);
  const industryPrev = rows.reduce((t, e) => t + n(e.prev), 0);
  const prevTotal = rows.reduce((t, e) => t + n(e.prev_total), 0);
  const mine = rows.find((e) => e.mine) ?? null;
  const mineCur = n(mine?.cur);
  const minePrev = n(mine?.prev);
  const shareCur = mine ? ratio(mineCur, industryCur) : null;
  const sharePrev = mine ? ratio(minePrev, industryPrev) : null;
  // De lo zarpado a la fecha, lo que todavía no llega (siempre por zarpe).
  const t = data.transit;
  const pending = n(t?.d1_7) + n(t?.d8_14) + n(t?.d15);
  const arrived = n(t?.arrived);
  const shipped = arrived + pending + n(t?.unknown);
  const minePrevTotal = n(mine?.prev_total);
  return {
    arrived,
    shipped,
    arrivedShare: ratio(arrived, shipped),
    mineArrived: n(t?.mine_arrived),
    mineShipped: n(t?.mine_total),
    mineArrivedShare: ratio(n(t?.mine_arrived), n(t?.mine_total)),
    pending,
    pendingShare: ratio(pending, shipped),
    prevPendingShare: ratio(n(t?.prev_pending), n(t?.prev_total)),
    minePending: n(t?.mine_pending),
    minePendingShare: ratio(n(t?.mine_pending), n(t?.mine_total)),
    industryCur,
    industryPrev,
    industryGrowth: growth(industryCur, industryPrev),
    prevTotal,
    // Avance respecto del cierre de la temporada anterior.
    progressOfPrevClose: ratio(industryCur, prevTotal),
    hasMine: !!mine,
    mineCur,
    minePrev,
    mineGrowth: mine ? growth(mineCur, minePrev) : null,
    minePrevTotal,
    mineProgressOfPrevClose: mine ? ratio(mineCur, minePrevTotal) : null,
    rank: rankOf(rows, (e) => n(e.cur)),
    rankPrev: rankOf(rows, (e) => n(e.prev)),
    shareCur,
    sharePrev,
    shareDeltaPts:
      shareCur != null && sharePrev != null ? (shareCur - sharePrev) * 100 : null,
  };
};

export type CurveMode = 'cumulative' | 'weekly';
export type CurveRow = { s: string; i: number; v?: string; k: number };

// Una serie por temporada, semana a semana. La temporada actual llega hasta el
// corte. rows: la curva de una variedad que no viene en el pulso
// (get_industry_volume_curves); si no, data.curves filtrada por variety.
export const curveSeries = (
  data: PulseData,
  {
    seasons,
    variety,
    mode,
    weeks,
    rows,
  }: { seasons: string[]; variety: string | null; mode: CurveMode; weeks: number; rows?: CurveRow[] }
) => {
  const bySeason = new Map<string, number[]>();
  for (const c of rows ?? data.curves ?? []) {
    if (!rows && variety && c.v !== variety) continue;
    const arr = bySeason.get(c.s) ?? new Array(weeks).fill(0);
    if (c.i >= 1 && c.i <= weeks) arr[c.i - 1] += c.k;
    bySeason.set(c.s, arr);
  }
  return seasons
    .filter((season) => bySeason.has(season))
    .map((season) => {
      const weekly = bySeason.get(season)!;
      const isCurrent = season === data.season;
      let acc = 0;
      const values = weekly.map((k, index) => {
        if (isCurrent && index + 1 > n(data.cutoff_index)) return null;
        acc += k;
        return mode === 'cumulative' ? acc : k;
      });
      return { season, isCurrent, values };
    });
};

export const varietyMix = (data: PulseData) => {
  const rows = data.varieties ?? [];
  const totalCur = rows.reduce((t, r) => t + n(r.cur), 0);
  const totalPrev = rows.reduce((t, r) => t + n(r.prev), 0);
  const totalMine = rows.reduce((t, r) => t + n(r.mine), 0);
  const shares = (cur: number, prev: number, mine: number) => {
    const shareCur = ratio(cur, totalCur) ?? 0;
    const sharePrev = ratio(prev, totalPrev) ?? 0;
    const mineShare = totalMine ? mine / totalMine : null;
    return {
      kilos: cur,
      // Variación de kilos contra la misma semana de la temporada anterior.
      growth: growth(cur, prev),
      shareCur,
      sharePrev,
      deltaPts: (shareCur - sharePrev) * 100,
      mineShare,
      mineVsIndustryPts: mineShare == null ? null : (mineShare - shareCur) * 100,
    };
  };
  // "Otras variedades" se abre en las que la componen.
  const others = (data.varieties_other ?? [])
    .map((o) => ({ variety: o.v, ...shares(n(o.cur), n(o.prev), n(o.mine)) }))
    .filter((o) => o.kilos > 0 || o.sharePrev > 0)
    .sort((a, b) => b.kilos - a.kilos);
  return rows
    .map((r) => ({
      variety: r.v,
      ...shares(n(r.cur), n(r.prev), n(r.mine)),
      peakCur: r.peak_cur,
      peakPrev: r.peak_prev,
      others: r.v === OTHERS ? others : [],
    }))
    .filter((r) => r.kilos > 0 || r.sharePrev > 0)
    .sort((a, b) =>
      a.variety === OTHERS ? 1 : b.variety === OTHERS ? -1 : b.kilos - a.kilos
    );
};

export type RankingCategory =
  | 'volume'
  | 'growth'
  | 'share'
  | 'nonChina'
  | 'air'
  | `variety:${string}`;

// Crecimiento solo entre las que superan este volumen, para que una
// exportadora chica no encabece con +500%.
export const MIN_KILOS_FOR_GROWTH = 1_000_000;

export const ranking = (data: PulseData, category: RankingCategory, size = 10) => {
  const rows = mergedExporters(data);
  const industryCur = rows.reduce((t, e) => t + n(e.cur), 0);
  const industryPrev = rows.reduce((t, e) => t + n(e.prev), 0);
  const shareOf = (e: PulseExporter) => ratio(n(e.cur), industryCur) ?? 0;
  const sharePrevOf = (e: PulseExporter) => ratio(n(e.prev), industryPrev) ?? 0;

  let metric: (e: PulseExporter) => number | null;
  let eligible: (e: PulseExporter) => boolean = (e) => n(e.cur) > 0;
  if (category === 'volume') metric = (e) => n(e.cur);
  else if (category === 'growth') {
    metric = (e) => growth(n(e.cur), n(e.prev));
    eligible = (e) => n(e.cur) >= MIN_KILOS_FOR_GROWTH && n(e.prev) > 0;
  } else if (category === 'share') metric = (e) => (shareOf(e) - sharePrevOf(e)) * 100;
  else if (category === 'nonChina') {
    metric = (e) => n(e.cur_non_china);
    eligible = (e) => n(e.cur_non_china) > 0;
  } else if (category === 'air') {
    metric = (e) => n(e.cur_air);
    eligible = (e) => n(e.cur_air) > 0;
  } else {
    const variety = category.slice('variety:'.length);
    metric = (e) => n(e.by_variety?.[variety]);
    eligible = (e) => n(e.by_variety?.[variety]) > 0;
  }

  const sorted = rows
    .filter(eligible)
    .sort((a, b) => n(metric(b)) - n(metric(a)) || n(b.cur) - n(a.cur));
  const toRow = (e: PulseExporter, index: number) => ({
    position: index + 1,
    key: e.key,
    // En mayúsculas, como el resto de la tabla.
    name: (e.mine ? e.name : displayName(e.name)).toLocaleUpperCase('es'),
    mine: e.mine,
    cur: n(e.cur),
    prev: n(e.prev),
    growth: growth(n(e.cur), n(e.prev)),
    share: shareOf(e),
    metric: metric(e),
  });
  const top = sorted.slice(0, size).map(toRow);
  const mineIndex = sorted.findIndex((e) => e.mine);
  // La exportadora del usuario siempre se ve: al final si no está en el top.
  const mine = mineIndex >= size ? toRow(sorted[mineIndex], mineIndex) : null;
  return { rows: top, mine, eligible: sorted.length, industryCur, industryPrev };
};

export const marketRegions = (data: PulseData) => {
  const rows = data.markets ?? [];
  const totalCur = rows.reduce((t, r) => t + n(r.cur), 0);
  const totalPrev = rows.reduce((t, r) => t + n(r.prev), 0);
  const totalMine = rows.reduce((t, r) => t + n(r.mine), 0);
  const shares = (cur: number, prev: number, mine: number) => {
    const shareCur = ratio(cur, totalCur) ?? 0;
    const sharePrev = ratio(prev, totalPrev) ?? 0;
    const mineShare = totalMine ? mine / totalMine : null;
    return {
      kilos: cur,
      // Variación de kilos contra la misma semana de la temporada anterior.
      growth: growth(cur, prev),
      shareCur,
      sharePrev,
      deltaPts: (shareCur - sharePrev) * 100,
      mineShare,
      mineVsIndustryPts: mineShare == null ? null : (mineShare - shareCur) * 100,
    };
  };
  const sum = (list: typeof rows) => ({
    cur: list.reduce((t, c) => t + n(c.cur), 0),
    prev: list.reduce((t, c) => t + n(c.prev), 0),
    mine: list.reduce((t, c) => t + n(c.mine), 0),
  });
  const groupBy = (list: typeof rows, key: (r: (typeof rows)[number]) => string) => {
    const groups = new Map<string, typeof rows>();
    list.forEach((r) => groups.set(key(r), [...(groups.get(key(r)) ?? []), r]));
    return Array.from(groups);
  };
  const visible = <T extends { kilos: number; sharePrev: number }>(list: T[]) =>
    list.filter((x) => x.kilos > 0 || x.sharePrev > 0).sort((a, b) => b.kilos - a.kilos);
  // Región → país → puerto de descarga.
  return groupBy(rows, (r) => r.region).map(([region, regionRows]) => {
    const r = sum(regionRows);
    // Países con volumen esta temporada (los de solo la anterior se listan, pero no se cuentan).
    const activeCountries = new Set(regionRows.filter((x) => (x.cur ?? 0) > 0).map((x) => x.country)).size;
    return {
      region,
      activeCountries,
      ...shares(r.cur, r.prev, r.mine),
      countries: visible(
        groupBy(regionRows, (x) => x.country).map(([country, countryRows]) => {
          const c = sum(countryRows);
          return {
            country,
            ...shares(c.cur, c.prev, c.mine),
            ports: visible(
              groupBy(countryRows, (x) => x.port ?? '—').map(([port, portRows]) => {
                const p = sum(portRows);
                return { port, ...shares(p.cur, p.prev, p.mine) };
              })
            ),
          };
        })
      ),
    };
  })
    .filter((r) => r.kilos > 0 || r.sharePrev > 0)
    .sort((a, b) => b.kilos - a.kilos);
};

const DAY_MS = 24 * 60 * 60 * 1000;
// Lunes de la semana ISO de un año (en milisegundos UTC).
export const isoWeekMonday = (year: number, week: number) => {
  const jan4 = Date.UTC(year, 0, 4);
  return jan4 - ((new Date(jan4).getUTCDay() + 6) % 7) * DAY_MS + (week - 1) * 7 * DAY_MS;
};

// Semana de cada punto de la temporada respecto del Año Nuevo Chino (0 = la
// semana del ANC, negativas antes), como en Mercado › Evolución.
export const weeksFromChineseNewYear = (season: string, weeks: number[], newYear: Date) => {
  const [startYear, endYear] = season.split('-').map(Number);
  const ms = Date.UTC(newYear.getUTCFullYear(), newYear.getUTCMonth(), newYear.getUTCDate());
  const newYearMonday = ms - ((new Date(ms).getUTCDay() + 6) % 7) * DAY_MS;
  return weeks.map((w) =>
    Math.round((isoWeekMonday(w >= 27 ? startYear : endYear, w) - newYearMonday) / (7 * DAY_MS))
  );
};
